import React, { useState, useRef, useCallback, useEffect } from "react";
import { MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsOut, ArrowsHorizontal, ArrowClockwise, CaretLeft, CaretRight } from "@phosphor-icons/react";

/**
 * The one viewer every screen uses to show a receipt. (ZEN-513)
 *
 * WHAT WAS WRONG. It shrank a picture until the WHOLE sheet fitted the box,
 * across AND top to bottom. A till receipt is a long narrow ribbon, so its
 * height always lost: measured in a real browser on a 30-item shop receipt, the
 * picture opened at 13% of life size and used a fifth of the width it had, and
 * the owner could not read her own receipts. Widening the panel made it
 * RELATIVELY smaller, because only the height was ever deciding. And anything
 * past the edge of the box was hidden, so there was nothing to scroll: the only
 * way down a long receipt was to zoom in and drag, and the natural act of
 * scrolling shrank the receipt instead.
 *
 * WHAT IT DOES NOW. Fits the WIDTH by default and scrolls inside its own panel,
 * so the fields beside it never move. Fitting the whole page stays as a choice.
 * The wheel scrolls; a held modifier key zooms, which is also what a trackpad
 * pinch sends. Dragging still moves the picture, the page arrows still turn
 * pages, and the panel still grows with the window.
 *
 * TWO THINGS MAKE IT SIMPLE, and both are worth keeping that way.
 *
 * 1. THE PICTURE IS DRAWN AT A REAL SIZE, not with a CSS scaling transform. A
 *    transformed image takes up no space on the page, so its box can never
 *    scroll however tall the image looks. Real width and height is what makes
 *    the scrollbar appear, and it is what lets the browser do the scrolling.
 *
 * 2. THE BROWSER KEEPS THE POSITION. There is no second record of where the
 *    reader is — the panel's own scroll offset is the only one. Dragging and the
 *    wheel both move it, so they cannot disagree.
 *
 * Props:
 *   getPageSrc    – function (pageNum) => url string, preview URL for a page
 *   pageCount     – total number of pages (default 1)
 *   alt           – alt text
 *   className     – wrapper className
 *   areaClassName – height/size classes for the viewing area itself. Defaults to
 *                   the 60% of the window height every existing caller has always
 *                   had. The row-level attachment panel (ZEN-351) passes a
 *                   shorter one on small screens, because a viewer 60% of a
 *                   phone's height leaves nothing for the row it is being
 *                   compared against — and keeping that row on screen is the
 *                   entire point of opening it from the row.
 *   errorMessage  – what to say when the file will not load. Defaults to the
 *                   line this component has always shown; callers that know
 *                   more (a file missing from storage, a picture format the
 *                   browser cannot open) say the more useful thing.
 *
 * Use key={receiptId} on the parent to trigger a full remount when switching receipts.
 */

// Breathing room so the sheet does not touch the panel edge — the gutter this
// component has always used.
const GUTTER = 16;
const MIN_SCALE = 0.05;
const MAX_SCALE = 5;
const clampScale = (s) => Math.min(Math.max(s, MIN_SCALE), MAX_SCALE);

export function ReceiptViewer({
  getPageSrc, pageCount = 1, alt = "Receipt", className = "",
  areaClassName = "h-[60vh]",
  errorMessage = "Could not load receipt preview",
  onPageError,
}) {
  const [currentPage, setCurrentPage] = useState(0);
  const [scale, setScale] = useState(1);
  // FIT WIDTH IS THE DEFAULT — owner decision, Maryse 2026-09-13 on ZEN-513.
  // Every receipt opens fitted to the width and scrolls; fitting the whole sheet
  // is a choice the reader makes, never the state they are given. Null once they
  // set a zoom of their own, so the panel stops re-fitting under them.
  const [fitMode, setFitMode] = useState("width");
  const [natural, setNatural] = useState(null);
  // A RECEIPT PHOTOGRAPHED SIDEWAYS. Nothing in this app turned anything before
  // — not here, and not the server, which reads no orientation from a photograph
  // at all. A phone snap usually carries a hidden "this way up" tag the browser
  // honours; a scan, a screenshot or a PDF page saved sideways does not, and the
  // only remedy was to tilt your head. Quarter turns clockwise, 0/90/180/270.
  const [rotation, setRotation] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  const totalPages = Math.max(pageCount || 1, 1);
  const imgSrc = getPageSrc(currentPage);

  // A QUARTER TURN SWAPS WHICH SIDE IS ACROSS THE PANEL, so every fit measures
  // the side that is now across it. Get this wrong and a sideways receipt fits
  // to its original width — it looks turned and reads no better.
  const quarterTurned = rotation === 90 || rotation === 270;
  const measure = useCallback(() => {
    const el = containerRef.current;
    const img = imgRef.current;
    if (!el || !img || !img.naturalWidth || !img.naturalHeight) return null;
    const turned = rotation === 90 || rotation === 270;
    return {
      cw: el.clientWidth, ch: el.clientHeight,
      iw: turned ? img.naturalHeight : img.naturalWidth,
      ih: turned ? img.naturalWidth : img.naturalHeight,
    };
  }, [rotation]);

  // Fit the WIDTH: the height is free to run past the bottom, where the reader
  // scrolls to it. Never blown up past life size — an enlarged small photo is
  // blurrier, not more readable.
  const fitWidthScale = useCallback(() => {
    const m = measure();
    if (!m) return 1;
    return Math.max(Math.min((m.cw - GUTTER) / m.iw, 1), MIN_SCALE);
  }, [measure]);

  // Fit the WHOLE sheet. Kept because it is the right choice for a glance at a
  // one-page invoice — just not the default.
  const fitWholeScale = useCallback(() => {
    const m = measure();
    if (!m) return 1;
    return Math.max(Math.min((m.cw - GUTTER) / m.iw, (m.ch - GUTTER) / m.ih, 1), MIN_SCALE);
  }, [measure]);

  const applyFit = useCallback((mode) => {
    setFitMode(mode);
    setScale(mode === "whole" ? fitWholeScale() : fitWidthScale());
    const el = containerRef.current;
    if (el) { el.scrollTop = 0; el.scrollLeft = 0; }
  }, [fitWholeScale, fitWidthScale]);

  const fitToWidth = useCallback(() => applyFit("width"), [applyFit]);
  const fitToWindow = useCallback(() => applyFit("whole"), [applyFit]);

  // Zooming leaves the fit, because a reader who chose a size must not have it
  // taken off them by the next resize.
  const zoomBy = useCallback((delta) => {
    setFitMode(null);
    setScale((s) => clampScale(s + delta));
  }, []);
  const zoomIn = () => zoomBy(0.25);
  const zoomOut = () => zoomBy(-0.25);

  // Turning re-fits, because the side that matters has just changed, and starts
  // at the top of the newly-shaped sheet.
  const rotateQuarterTurn = () => {
    setRotation((r) => (r + 90) % 360);
    const el = containerRef.current;
    if (el) { el.scrollTop = 0; el.scrollLeft = 0; }
  };

  const goToPrevPage = () => { if (currentPage > 0) setCurrentPage((p) => p - 1); };
  const goToNextPage = () => { if (currentPage < totalPages - 1) setCurrentPage((p) => p + 1); };

  // THE WHEEL SCROLLS. It used to zoom, so the natural act of scrolling down a
  // long receipt shrank it — measured on the old code, two scrolls took a
  // receipt from 13% to 5%. Now the browser scrolls the panel and only a held
  // modifier key zooms. A trackpad pinch arrives as a wheel with ctrl held, so
  // pinching zooms without any extra handling.
  const onWheel = useCallback((e) => {
    if (!(e.ctrlKey || e.metaKey)) return;   // plain wheel: let the panel scroll
    e.preventDefault();
    zoomBy(e.deltaY > 0 ? -0.12 : 0.12);
  }, [zoomBy]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Not passive: a zoom gesture has to be able to stop the browser doing its
    // own page zoom. A plain scroll returns before preventDefault is reached.
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  // THE PANEL GROWS WITH THE WINDOW. Re-fit when it changes size, so opening a
  // sidebar or turning a laptop does not leave the receipt at a size chosen for
  // a panel that no longer exists. A reader who set their own zoom is left alone.
  //
  // A SCROLLBAR APPEARING IS NOT A RESIZE. Fitting the width reads the space
  // left for content, and on Windows and Linux a scrollbar eats that space — so
  // fitting could make the scrollbar appear, which narrows the panel, which
  // shrinks the picture, which removes the scrollbar, for ever. Worked through
  // on a near-square receipt in a 558x538 panel: 558 -> 543 -> 558 with no
  // resting place. The OUTER box is what a scrollbar cannot change, so that is
  // what decides whether anything really resized.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !loaded || !fitMode) return;
    if (typeof ResizeObserver === "undefined") return;
    let outerW = el.offsetWidth, outerH = el.offsetHeight;
    const ro = new ResizeObserver(() => {
      if (el.offsetWidth === outerW && el.offsetHeight === outerH) return;
      outerW = el.offsetWidth; outerH = el.offsetHeight;
      setScale(fitMode === "whole" ? fitWholeScale() : fitWidthScale());
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitMode, loaded, fitWholeScale, fitWidthScale]);

  // DRAG MOVES THE SCROLL, it does not move the picture. Both gestures write to
  // the one position the browser already keeps, so grabbing the sheet and
  // scrolling it cannot disagree about where the reader is.
  const onMouseDown = (e) => {
    const el = containerRef.current;
    if (e.button !== 0 || !el) return;
    // Not when the reader is grabbing the scrollbar: where scrollbars take real
    // space the bar sits inside this element, and starting a drag there fights
    // the browser for the same movement.
    const r = e.currentTarget.getBoundingClientRect();
    if (e.clientX - r.left >= el.clientWidth || e.clientY - r.top >= el.clientHeight) return;
    setDragging(true);
    dragRef.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
  };
  const onMouseMove = (e) => {
    const el = containerRef.current;
    if (!dragging || !el || !dragRef.current) return;
    el.scrollLeft = dragRef.current.left - (e.clientX - dragRef.current.x);
    el.scrollTop = dragRef.current.top - (e.clientY - dragRef.current.y);
  };
  const onMouseUp = () => { setDragging(false); dragRef.current = null; };

  const onImgLoad = (e) => {
    const img = e.currentTarget;
    // A PICTURE WITH NO SIZE CANNOT BE SHOWN, and pretending otherwise is worse
    // than saying so. A width or height of zero — a broken file, or a drawing
    // with no size of its own — would multiply every later measurement by zero:
    // the sheet renders as nothing at all, and zooming cannot recover it. So it
    // takes the same road as a file that will not load, which already says what
    // happened in the caller's own words.
    if (!(img.naturalWidth > 0) || !(img.naturalHeight > 0)) { setError(true); return; }
    setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    setLoaded(true);
  };

  // Fit once the picture's real size is known. Kept as an effect rather than
  // done inside the load handler so it reads the size that is actually on the
  // page, not the one that is about to be.
  // ONE PLACE DECIDES THE SIZE when something about the picture changes: a new
  // one arrives, or it is turned. There used to be two nearly identical effects
  // here, one per cause, which is the shape that lets two answers drift apart —
  // and review read them as exactly that risk. A single list of causes cannot.
  //
  // A reader who set their own zoom is on no fit and is left alone.
  useEffect(() => {
    if (!loaded || !natural || !fitMode) return;
    setScale(fitMode === "whole" ? fitWholeScale() : fitWidthScale());
    const el = containerRef.current;
    if (el) { el.scrollTop = 0; el.scrollLeft = 0; }
    // Deliberately not the fit functions themselves: this fires when the
    // PICTURE changes, not every time a fit is recalculated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, natural, rotation]);

  // Turning to page 2 starts you at the top of it, at the same fit the reader
  // was already using — the fit is a preference, not a property of one page.
  useEffect(() => {
    setNatural(null); setLoaded(false); setError(false);
    const el = containerRef.current;
    if (el) { el.scrollTop = 0; el.scrollLeft = 0; }
  }, [currentPage]);

  if (error) {
    return (
      <div className={`flex items-center justify-center rounded-lg border border-[#C2C9D1] bg-[#F8FAFC] p-8 text-sm text-[#6b7280] ${className}`}>
        {errorMessage}
      </div>
    );
  }

  const pct = Math.round(scale * 100);
  const tabClass = (on) =>
    `rounded px-2 py-1 text-[11px] font-semibold ${on ? "bg-[#E9E9EB] text-[#1f2937]" : "text-[#6b7280] hover:bg-[#E9E9EB] hover:text-[#1f2937]"}`;

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Toolbar */}
      {/* THE ROW WRAPS. Every control on this line is a child of one flex
          row, and a flex row does not wrap unless it is told to — so in a
          column narrower than the controls need, the last one (Rotate) was
          painted OUTSIDE the panel, on top of whatever sat beside it. On the
          approval screen that was the Date box, and the right two-fifths of
          Rotate stopped being clickable at all: the press landed in the date
          field. Measured on the running app at 1024, 1280 and 1440 wide, and
          on a 320-wide phone, where the panel is already full width and no
          amount of widening could have helped. Wrapping keeps the overflow
          INSIDE the panel, on a second line, wherever this viewer is used.
          (ZEN-666) */}
      <div className="flex flex-wrap items-center gap-1 rounded-t-lg border border-b-0 border-[#C2C9D1] bg-[#F8FAFC] px-2 py-1.5">
        <button onClick={zoomOut} className="rounded p-1.5 text-[#6b7280] hover:bg-[#E9E9EB] hover:text-[#1f2937]" title="Zoom out" data-testid="receipt-zoom-out">
          <MagnifyingGlassMinus size={16} weight="bold" />
        </button>
        <span className="min-w-[3rem] text-center text-xs font-semibold text-[#6b7280]" data-testid="receipt-zoom-level">{pct}%</span>
        <button onClick={zoomIn} className="rounded p-1.5 text-[#6b7280] hover:bg-[#E9E9EB] hover:text-[#1f2937]" title="Zoom in" data-testid="receipt-zoom-in">
          <MagnifyingGlassPlus size={16} weight="bold" />
        </button>
        <div className="mx-1 h-4 w-px bg-[#C2C9D1]" />
        {/* "Fit width" and "Fit page" are the OWNER'S WORDS — approved by Maryse
            on 2026-09-13 on ZEN-513, where the decision is recorded. "Fit" alone
            used to mean fit the whole sheet; there are two fits now, so both had
            to be named. Do not reword either without asking her. The hover text
            repeats the label rather than explaining it, because an explaining
            sentence would be new copy and copy is hers. */}
        <button onClick={fitToWidth} aria-pressed={fitMode === "width"} className={tabClass(fitMode === "width")} title="Fit width" data-testid="receipt-fit-width">
          <ArrowsHorizontal size={14} weight="bold" className="mr-1 inline -mt-px" />Fit width
        </button>
        <button onClick={fitToWindow} aria-pressed={fitMode === "whole"} className={tabClass(fitMode === "whole")} title="Fit page" data-testid="receipt-fit-reset">
          <ArrowsOut size={14} weight="bold" className="mr-1 inline -mt-px" />Fit page
        </button>

        {/* "Rotate" is the OWNER'S WORD — approved by Maryse on 2026-09-13 on
            ZEN-513. One button, a quarter turn clockwise each press, round to
            upright in four. Not saved against the receipt: this turns how it is
            READ, and changing the stored file is a different decision nobody has
            asked for. */}
        <div className="mx-1 h-4 w-px bg-[#C2C9D1]" />
        <button onClick={rotateQuarterTurn} className="rounded px-2 py-1 text-[11px] font-semibold text-[#6b7280] hover:bg-[#E9E9EB] hover:text-[#1f2937]" title="Rotate" data-testid="receipt-rotate">
          <ArrowClockwise size={14} weight="bold" className="mr-1 inline -mt-px" />Rotate
        </button>

        {/* Page navigation — only shown for multi-page documents.
            THE ARROWS AND THE COUNT WRAP AS ONE PIECE. When the row wraps, each
            loose child wraps on its own, so in the approval panel the "<" was
            left behind at the end of the first line while "1 / 8 >" dropped to
            the second. Held in one no-wrap group, the three move together and
            always read "< 1 of 8 >". "1 of 8" is the OWNER'S WORDING — Maryse,
            2026-09-27. */}
        {totalPages > 1 && (
          <>
            <div className="mx-1 h-4 w-px bg-[#C2C9D1]" />
            <div className="flex shrink-0 items-center whitespace-nowrap" data-testid="receipt-page-nav">
              <button onClick={goToPrevPage} disabled={currentPage === 0}
                className="rounded p-1.5 text-[#6b7280] hover:bg-[#E9E9EB] hover:text-[#1f2937] disabled:opacity-30 disabled:cursor-not-allowed"
                title="Previous page" data-testid="receipt-page-prev">
                <CaretLeft size={14} weight="bold" />
              </button>
              <span className="min-w-[4rem] text-center text-xs font-semibold text-[#6b7280]" data-testid="receipt-page-indicator">
                {currentPage + 1} of {totalPages}
              </span>
              <button onClick={goToNextPage} disabled={currentPage >= totalPages - 1}
                className="rounded p-1.5 text-[#6b7280] hover:bg-[#E9E9EB] hover:text-[#1f2937] disabled:opacity-30 disabled:cursor-not-allowed"
                title="Next page" data-testid="receipt-page-next">
                <CaretRight size={14} weight="bold" />
              </button>
            </div>
          </>
        )}
      </div>
      {/* Viewer area — scrolls INSIDE its own box, so the fields beside it never
          move while the reader goes down a long receipt. */}
      <div
        ref={containerRef}
        className={`relative overflow-auto rounded-b-lg border border-[#C2C9D1] bg-[#1f2937] ${areaClassName}`}
        style={{
          cursor: dragging ? "grabbing" : "grab",
          // Let the browser do touch scrolling and pinch itself; it does it
          // better than a hand-written pan, with momentum.
          touchAction: "pan-x pan-y pinch-zoom",
          // THE BOX NEVER TAKES ITS WIDTH FROM THE PICTURE. In a table row the
          // cell grew to fit the zoomed picture, so the box had nothing to
          // scroll or drag sideways and its scroll bars sat off screen. (ZEN-808)
          contain: "inline-size",
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        data-testid="receipt-viewer-area"
      >
        {!loaded && !error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          </div>
        )}
        {/* w-max keeps a picture wider than the panel at its full width — without
            it, centring clips the left edge out of reach of the scrollbar. */}
        <div className="flex w-max min-w-full min-h-full items-center justify-center">
          {/* THE BOX THAT TAKES UP THE SPACE. A CSS rotation is drawn turned but
              still OCCUPIES its upright shape, so a sideways receipt would look
              right and still scroll as though it were standing up. This frame
              carries the TURNED shape — width and height swapped on a quarter
              turn — so the panel scrolls the way the picture actually lies. */}
          <div
            style={{
              width: natural ? `${(quarterTurned ? natural.h : natural.w) * scale}px` : undefined,
              height: natural ? `${(quarterTurned ? natural.w : natural.h) * scale}px` : undefined,
              position: "relative",
              display: loaded ? "block" : "none",
            }}
            data-testid="receipt-viewer-frame"
          >
          <img
            ref={imgRef}
            src={imgSrc}
            alt={alt}
            draggable={false}
            onLoad={onImgLoad}
            onError={() => { setError(true); onPageError?.(currentPage); }}
            className="select-none"
            style={{
              // A REAL size on the page, not a scaling transform. A transformed
              // image takes up no space, so its panel could never scroll.
              width: natural ? `${natural.w * scale}px` : undefined,
              height: natural ? `${natural.h * scale}px` : undefined,
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
              transformOrigin: "center center",
              maxWidth: "none",
              display: loaded ? "block" : "none",
            }}
            data-testid="receipt-viewer-image"
          />
          </div>
        </div>
      </div>
    </div>
  );
}
